import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import config from "./config";
import OTPInput from "./components/OTPInput";
import './styles/Register.css';
import AvatarPicker from "./components/AvatarPicker";

function Register({ setUser }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [bio, setBio] = useState("");
  const [otp, setOtp] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null); // For avatar selection
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [registerStep, setRegisterStep] = useState(1);

  // Check for access restriction error from URL params (Google OAuth)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const message = urlParams.get('message');
    
    if (error === 'access_restricted' && message) {
      setErrorMessage(decodeURIComponent(message));
      setShowError(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Add class to body for auth page scrolling
    document.body.classList.add('auth-page-active');
    
    return () => {
      document.body.classList.remove('auth-page-active');
    };
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      setAvatarUrl(null); // Clear avatar if uploading image
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle avatar selection
  const handlePickAvatar = (dataUrl) => {
    setAvatarUrl(dataUrl);
    setProfileImage(null);
    setImagePreview(dataUrl);
  };

  const handleNextStep = (e) => {
    e.preventDefault();

    if (!username.trim() || !email.trim()) {
      setErrorMessage("Please fill in all required fields");
      setShowError(true);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setErrorMessage("Please enter a valid email address");
      setShowError(true);
      return;
    }

    setRegisterStep(2);
  };

  const handlePrevStep = () => {
    setRegisterStep(1);
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!username || !email) {
      setErrorMessage("Please fill in all required fields");
      setShowError(true);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${config.apiUrl}/api/auth/send-registration-otp`, {
        username,
        email
      });
      setOtpSent(true);
      setErrorMessage("");
    } catch (error) {
      console.error('Send OTP error:', error);
      const errorMsg = error.response?.data?.error || 'Failed to send OTP. Please try again.';
      setErrorMessage(errorMsg);
      setShowError(true);
      // If access is restricted, don't proceed to OTP screen
      if (error.response?.status === 403) {
        setOtpSent(false);
      }
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otp) {
      setErrorMessage("Please enter the OTP");
      setShowError(true);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('email', email);
      formData.append('bio', bio);
      formData.append('otp', otp);
      
      if (profileImage) {
        formData.append('profileImage', profileImage);
      } else if (avatarUrl) {
        // Convert dataUrl to Blob
        const res = await fetch(avatarUrl);
        const blob = await res.blob();
        formData.append('profileImage', blob, 'avatar.svg');
      }

      const res = await axios.post(`${config.apiUrl}/api/auth/verify-registration-otp`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Automatically log in after registration
      const userData = { 
        id: res.data.user.id, 
        username: res.data.user.username,
        email: res.data.user.email,
        profileImage: res.data.user.profileImage,
        isVerified: res.data.user.isVerified
      };
      
      // Fetch privacy settings for the new user
      try {
        const privacyRes = await axios.get(`${config.apiUrl}/api/auth/privacy-settings/${userData.id}`);
        const userWithPrivacy = {
          ...userData,
          privacySettings: privacyRes.data.privacySettings
        };
        setUser(userWithPrivacy);
        localStorage.setItem("chatapp_user", JSON.stringify(userWithPrivacy));
      } catch (privacyErr) {
        console.error('Failed to load privacy settings:', privacyErr);
        // Continue with user data without privacy settings
        setUser(userData);
        localStorage.setItem("chatapp_user", JSON.stringify(userData));
      }
      
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error);
      setErrorMessage(error.response?.data?.error || 'Registration failed. Please try again.');
      setShowError(true);
    }
    setLoading(false);
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      await axios.post(`${config.apiUrl}/api/auth/send-registration-otp`, {
        username,
        email
      });
      setErrorMessage("OTP resent successfully");
      setShowError(true);
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Failed to resend OTP");
      setShowError(true);
    }
    setLoading(false);
  };

  const handleQuickRegister = async (e) => {
    e.preventDefault();
    
    if (!username || !email) {
      setErrorMessage("Please fill in all required fields");
      setShowError(true);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('email', email);
      formData.append('bio', bio);
      
      if (profileImage) {
        formData.append('profileImage', profileImage);
      } else if (avatarUrl) {
        // Convert dataUrl to Blob
        const res = await fetch(avatarUrl);
        const blob = await res.blob();
        formData.append('profileImage', blob, 'avatar.svg');
      }

      const res = await axios.post(`${config.apiUrl}/api/auth/quick-register`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Automatically log in after registration
      const userData = { 
        id: res.data.user.id, 
        username: res.data.user.username,
        email: res.data.user.email,
        profileImage: res.data.user.profileImage,
        isVerified: res.data.user.isVerified
      };
      
      // Fetch privacy settings for the new user
      try {
        const privacyRes = await axios.get(`${config.apiUrl}/api/auth/privacy-settings/${userData.id}`);
        const userWithPrivacy = {
          ...userData,
          privacySettings: privacyRes.data.privacySettings
        };
        setUser(userWithPrivacy);
        localStorage.setItem("chatapp_user", JSON.stringify(userWithPrivacy));
      } catch (privacyErr) {
        console.error('Failed to load privacy settings:', privacyErr);
        // Continue with user data without privacy settings
        setUser(userData);
        localStorage.setItem("chatapp_user", JSON.stringify(userData));
      }
      
      navigate('/');
    } catch (error) {
      console.error('Quick registration error:', error);
      setErrorMessage(error.response?.data?.error || 'Quick registration failed. Please try again.');
      setShowError(true);
    }
    setLoading(false);
  };

  return (
    <div className="auth-page">
      <div className="auth-card-grid auth-card-grid--compact">
        <div className="auth-card-info">
          <div className="auth-logo-chip">UcmaApp</div>
          <h1>Join the UcmaApp</h1>
          <p>
            Build secure connections, sync conversations across devices, and collaborate with
            your team in real time.
          </p>
          {/* <ul className="auth-benefits">
            <li>OTP-protected onboarding for every member</li>
            <li>Voice, video, and status sharing built in</li>
            <li>Enterprise-grade privacy controls</li>
          </ul> */}
        </div>

        <div className="auth-container auth-container--panel auth-container--register">
          <h2>Create your account</h2>
          
          {!otpSent ? (
            <>
              <div className="register-step-indicator">
                <span className={`register-step-dot ${registerStep >= 1 ? 'active' : ''}`} />
                <span className={`register-step-dot ${registerStep >= 2 ? 'active' : ''}`} />
              </div>

              <form onSubmit={registerStep === 1 ? handleNextStep : handleSendOTP}>
                {registerStep === 1 && (
                  <>
                    <input
                      name="username"
                      placeholder="Username"
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      required
                    />
                    <input
                      name="email"
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                    />

                    <div className="register-step-nav">
                      <button
                        type="submit"
                        className="register-next-btn"
                        aria-label="Continue to profile details"
                      >
                        <span>Next</span>
                        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                          <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                        </svg>
                      </button>
                    </div>
                  </>
                )}

                {registerStep === 2 && (
                  <>
                    <button
                      type="button"
                      className="register-back-btn"
                      onClick={handlePrevStep}
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                        <path fill="currentColor" d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/>
                      </svg>
                      <span>Back</span>
                    </button>

                    <textarea
                      name="bio"
                      placeholder="Bio (Optional)"
                      value={bio}
                      onChange={e => setBio(e.target.value)}
                      maxLength="150"
                      rows="3"
                    />

                    <div className="image-upload-container">
                      <label htmlFor="profile-image" className="image-upload-label">
                        {imagePreview ? 'Change Profile Image' : 'Add Profile Image (Optional)'}
                      </label>
                      <input
                        id="profile-image"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />
                      {imagePreview && (
                        <div className="image-preview">
                          <button 
                            type="button" 
                            aria-label="Remove selected image"
                            className="image-remove-pill"
                            onClick={() => {
                              setProfileImage(null);
                              setAvatarUrl(null);
                              setImagePreview(null);
                            }}
                          >
                            <span>&times;</span>
                          </button>
                          <img src={imagePreview} alt="Profile preview" />
                        </div>
                      )}
                      <div className="register-avatar-picker">
                        <span>Or pick an avatar:</span>
                        <AvatarPicker onPick={handlePickAvatar} selected={avatarUrl} />
                      </div>
                    </div>
                    
                    <button type="submit" disabled={loading}>
                      {loading ? "Sending..." : "Send OTP"}
                    </button>

                    <div className="auth-divider">
                      <span>OR</span>
                    </div>

                    <button 
                      type="button" 
                      className="google-btn"
                      onClick={() => {
                        window.location.href = `${config.apiUrl}/api/auth/google`;
                      }}
                    >
                      <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continue with Google
                    </button>
                  </>
                )}
              </form>
            </>
          ) : (
            <form onSubmit={handleVerifyOTP}>
              <div className="auth-helper-text">
                OTP sent to {email}
              </div>
              <OTPInput value={otp} onChange={setOtp} length={4} />
              <button type="submit" disabled={loading}>
                {loading ? "Verifying..." : "Complete Registration"}
              </button>
              <button 
                type="button" 
                onClick={handleResendOTP}
                disabled={loading}
                className="auth-ghost-btn"
              >
                Resend OTP
              </button>

              <div className="auth-divider">
                <span>OR</span>
              </div>

              <button 
                type="button" 
                className="google-btn"
                onClick={() => {
                  window.location.href = `${config.apiUrl}/api/auth/google`;
                }}
              >
                <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
            </form>
          )}

          <div className="auth-switch">
            <span>Already have an account?</span>
            <Link to="/login">Login</Link>
          </div>
          
          <p className="auth-legal-text">
            By signing in, you agree to our{" "}
            <Link to="/terms">Terms of Service</Link> and{" "}
            <Link to="/privacy-policy">Privacy Policy</Link>.
          </p>
        </div>
      </div>

      {showError && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-message">{errorMessage}</div>
            <div className="modal-actions">
              <button className="modal-btn confirm" onClick={() => {
                setShowError(false);
                setErrorMessage("");
              }}>OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Register; 